import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { ScrollArea } from '../../components/ui/scroll-area';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Plus, Trash2, FolderTree, Search, ChevronRight, ChevronsUpDown,
  Pencil, MapPin, Users, Layers, Building2, GraduationCap, BookOpen, FlaskConical, Briefcase
} from 'lucide-react';
import { OrgTree } from './TreeNode';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPES = {
  rectorate: 'Rectorat', direction: 'Direction', faculty: 'Faculte', section: 'Section',
  department: 'Departement', program: 'Programme', center: 'Centre', service: 'Service',
  library: 'Bibliotheque', laboratory: 'Laboratoire', clinic: 'Clinique',
  residence: 'Residence', bureau: 'Bureau', cabinet: 'Cabinet', other: 'Autre'
};

const ICONS = {
  rectorate: Building2, direction: Building2, faculty: GraduationCap,
  department: Layers, center: FlaskConical, service: Briefcase, program: BookOpen,
};

export const StructuresTab = ({ institutionId }) => {
  const [tree, setTree] = useState([]);
  const [sel, setSel] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dlg, setDlg] = useState(false);
  const [editDlg, setEditDlg] = useState(false);
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [type, setType] = useState('department');
  const [pid, setPid] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [editName, setEditName] = useState('');
  const [editDesc, setEditDesc] = useState('');

  const load = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/core/institutions/${institutionId}/units-tree`); setTree(data); }
    catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [institutionId]);

  useEffect(() => { load(); }, [load]);

  // Load detail with breadcrumbs when selecting a unit
  const selectUnit = useCallback(async (node) => {
    setSel(node);
    try {
      const { data } = await axios.get(`${API}/core/institutions/${institutionId}/units/${node.id}`);
      setDetail(data);
    } catch (e) { setDetail(node); }
  }, [institutionId]);

  // Search
  useEffect(() => {
    if (!searchQuery || searchQuery.length < 2) { setSearchResults([]); return; }
    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const { data } = await axios.get(`${API}/core/institutions/${institutionId}/units-search?q=${encodeURIComponent(searchQuery)}`);
        setSearchResults(data);
      } catch (e) { setSearchResults([]); }
      finally { setSearching(false); }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, institutionId]);

  const create = async () => {
    try {
      await axios.post(`${API}/core/institutions/${institutionId}/units`, { name, code, type, parent_id: pid });
      toast.success(`"${name}" cree`);
      setDlg(false); setName(''); setCode(''); setType('department'); setPid(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const update = async () => {
    if (!detail) return;
    try {
      await axios.put(`${API}/core/institutions/${institutionId}/units/${detail.id}`, {
        name: editName || undefined, description: editDesc || undefined,
      });
      toast.success('Structure mise a jour');
      setEditDlg(false);
      load();
      selectUnit({ ...detail, name: editName || detail.name });
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const del = async (id) => {
    if (!window.confirm('Supprimer cette structure ?')) return;
    try { await axios.delete(`${API}/core/institutions/${institutionId}/units/${id}`); toast.success('Supprime'); setSel(null); setDetail(null); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const openEdit = () => {
    if (!detail) return;
    setEditName(detail.name || '');
    setEditDesc(detail.description || '');
    setEditDlg(true);
  };

  if (loading) return <div className="flex justify-center p-12"><div className="w-8 h-8 border-3 border-blue-600 border-t-transparent rounded-full animate-spin" /></div>;

  const showSearch = searchQuery.length >= 2;

  return (
    <div className="space-y-3" data-testid="structures-tab">
      {/* Search bar */}
      <div className="relative">
        <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
        <Input
          className="pl-9"
          placeholder="Rechercher une structure (faculte, departement, service...)"
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          data-testid="search-structures"
        />
        {showSearch && (
          <div className="absolute z-20 top-full left-0 right-0 mt-1 bg-white border rounded-lg shadow-lg max-h-64 overflow-y-auto" data-testid="search-results">
            {searching ? (
              <div className="p-4 text-center text-sm text-gray-400">Recherche...</div>
            ) : searchResults.length === 0 ? (
              <div className="p-4 text-center text-sm text-gray-400">Aucun resultat pour "{searchQuery}"</div>
            ) : (
              searchResults.map(r => {
                const Icon = ICONS[r.type] || Layers;
                return (
                  <button
                    key={r.id}
                    className="w-full text-left px-3 py-2 hover:bg-blue-50 flex items-center gap-2 border-b last:border-0"
                    onClick={() => { selectUnit(r); setSearchQuery(''); setSearchResults([]); }}
                    data-testid={`search-result-${r.id}`}
                  >
                    <Icon className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium truncate">{r.name}</p>
                      {r.path && <p className="text-xs text-gray-400 truncate">{r.path}</p>}
                    </div>
                    <Badge variant="outline" className="text-[10px] flex-shrink-0">{TYPES[r.type] || r.type}</Badge>
                  </button>
                );
              })
            )}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Tree */}
        <Card className="lg:col-span-2">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <FolderTree className="w-4 h-4" /> Arbre ({tree.length})
              </CardTitle>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => { setPid(null); setDlg(true); }} data-testid="create-unit-btn">
                  <Plus className="w-4 h-4 mr-1" /> Ajouter
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[500px]">
              {tree.length === 0 ? (
                <p className="text-center text-gray-400 py-12 text-sm">Aucune structure. Cliquez "Ajouter" pour creer la premiere.</p>
              ) : (
                <OrgTree tree={tree} onSelect={selectUnit} selectedId={sel?.id} />
              )}
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Detail panel */}
        <Card>
          <CardHeader className="pb-3"><CardTitle className="text-base">Details</CardTitle></CardHeader>
          <CardContent>
            {detail ? (
              <div className="space-y-3" data-testid="unit-detail">
                {/* Breadcrumbs */}
                {detail.breadcrumb && detail.breadcrumb.length > 0 && (
                  <div className="flex flex-wrap items-center gap-1 text-xs text-gray-400" data-testid="unit-breadcrumb">
                    {detail.breadcrumb.map((b, i) => (
                      <React.Fragment key={b.id}>
                        <button
                          className="hover:text-blue-600 hover:underline"
                          onClick={() => selectUnit(b)}
                        >{b.name}</button>
                        <ChevronRight className="w-3 h-3" />
                      </React.Fragment>
                    ))}
                    <span className="text-gray-600 font-medium">{detail.name}</span>
                  </div>
                )}

                <div><p className="text-xs text-gray-500">Nom</p><p className="font-medium text-sm">{detail.name}</p></div>
                <div className="flex gap-2">
                  <Badge variant="secondary">{TYPES[detail.type] || detail.type}</Badge>
                  <Badge variant="outline" className="text-[10px]">Niveau {detail.level}</Badge>
                  {detail.code && <Badge variant="outline" className="text-[10px]">{detail.code}</Badge>}
                </div>
                {detail.description && (
                  <div><p className="text-xs text-gray-500">Description</p><p className="text-sm text-gray-600">{detail.description}</p></div>
                )}

                {/* Children */}
                {detail.children && detail.children.length > 0 && (
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Sous-structures ({detail.children.length})</p>
                    <div className="space-y-1">
                      {detail.children.map(c => {
                        const CIcon = ICONS[c.type] || Layers;
                        return (
                          <button
                            key={c.id}
                            className="w-full text-left flex items-center gap-2 px-2 py-1.5 rounded hover:bg-blue-50 text-sm"
                            onClick={() => selectUnit(c)}
                          >
                            <CIcon className="w-3.5 h-3.5 text-gray-400" />
                            <span className="truncate">{c.name}</span>
                            <Badge variant="outline" className="text-[9px] ml-auto flex-shrink-0">{TYPES[c.type] || c.type}</Badge>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className="flex flex-wrap gap-2 pt-2 border-t">
                  <Button size="sm" variant="outline" onClick={() => { setPid(detail.id); setDlg(true); }}>
                    <Plus className="w-3 h-3 mr-1" /> Sous-structure
                  </Button>
                  <Button size="sm" variant="outline" onClick={openEdit}>
                    <Pencil className="w-3 h-3 mr-1" /> Modifier
                  </Button>
                  <Button size="sm" variant="destructive" onClick={() => del(detail.id)}>
                    <Trash2 className="w-3 h-3 mr-1" /> Supprimer
                  </Button>
                </div>
              </div>
            ) : (
              <p className="text-sm text-gray-400 py-8 text-center">Selectionnez une structure dans l'arbre ou utilisez la recherche</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Create dialog */}
      <Dialog open={dlg} onOpenChange={setDlg}>
        <DialogContent data-testid="create-unit-dialog">
          <DialogHeader><DialogTitle>Nouvelle Structure</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Nom *</Label><Input value={name} onChange={e => setName(e.target.value)} placeholder="Ex: Departement d'Informatique" /></div>
            <div><Label>Code</Label><Input value={code} onChange={e => setCode(e.target.value)} placeholder="INFO" /></div>
            <div><Label>Type</Label>
              <Select value={type} onValueChange={setType}><SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(TYPES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent></Select></div>
            {pid && <Badge variant="outline">Parent: {detail?.name || sel?.name || pid}</Badge>}
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setDlg(false)}>Annuler</Button><Button onClick={create} disabled={!name}>Creer</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={editDlg} onOpenChange={setEditDlg}>
        <DialogContent data-testid="edit-unit-dialog">
          <DialogHeader><DialogTitle>Modifier la Structure</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Nom</Label><Input value={editName} onChange={e => setEditName(e.target.value)} /></div>
            <div><Label>Description</Label><Input value={editDesc} onChange={e => setEditDesc(e.target.value)} placeholder="Description..." /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setEditDlg(false)}>Annuler</Button><Button onClick={update}>Enregistrer</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
